import { useEffect, useState } from "react";
import type { StoryView } from "../shared/story";
import { api } from "./api";
import { ClothThumb } from "./cloth";
import { PlateTag } from "./plates";
import { Meanwhile, markMeanwhileSeen, meanwhileDue } from "./v2/meanwhile";

/**
 * The morning: the first time a player opens a story after the loom dreamt it, the dream is an event, not a notice.
 * "While you slept" (or "While you played", for a dream that came mid-session), the recap written out at the narrator's pace (tap to finish), the new plate developing like a
 * print, and a quiet word that something will come back (never which: that is the surprise). Seen once per dream,
 * remembered on this device.
 */

const seenKey = (id: string) => `loom:morning:${id}`;
const seenAt = (id: string) => { try { return Number(localStorage.getItem(seenKey(id)) ?? 0); } catch { return 0; } };
const markSeen = (id: string, at: number) => { try { localStorage.setItem(seenKey(id), String(at)); } catch { /* shown again next time */ } };

/** A dream the player has already seen elsewhere (the last plate, on the end card): no morning for it. */
export function markMorningSeen(view: StoryView): void { if (view.dreamt) markSeen(view.id, view.dreamt.at); }

/** Is there a dream this player hasn't woken up to yet? A brand-new story's first open counts as seen. */
export function morningDue(view: StoryView | null): boolean {
  if (!view?.dreamt) return false;
  return view.dreamt.at > seenAt(view.id);
}

const PACE = 40; // characters a second, like the narrator

// Under the first plate a player sees (this device), while plates still draw them from the story: where to choose.
const DRAW_NOTE_KEY = "loom:draw-as-note";
const drawNoteSeen = () => { try { return localStorage.getItem(DRAW_NOTE_KEY) === "1"; } catch { return true; } };
const markDrawNote = () => { try { localStorage.setItem(DRAW_NOTE_KEY, "1"); } catch { /* shown again next time */ } };

export function Morning({ view, inPlay = false, onDone }: { view: StoryView; inPlay?: boolean; onDone(): void }) {
  const since = seenAt(view.id);
  const plates = view.images.filter((p) => (p.drawnAt ?? 0) > since || (!p.drawnAt && p.at >= (view.dreamt?.n ?? 0)));
  const recap = view.recap.trim();
  const [shown, setShown] = useState(0);
  const [drawNote, setDrawNote] = useState(false);
  useEffect(() => {
    if (!plates.length || drawNoteSeen()) return;
    api.settings().then((r) => { if (r.drawAs === "story") setDrawNote(true); }, () => {});
  }, [plates.length]);
  const reduce = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
  useEffect(() => {
    if (reduce || shown >= recap.length) return;
    const t = window.setTimeout(() => setShown((c) => Math.min(recap.length, c + 2)), 2000 / PACE);
    return () => window.clearTimeout(t);
  }, [shown, recap, reduce]);
  const done = reduce || shown >= recap.length;
  useEffect(() => { if (done && drawNote) markDrawNote(); }, [done, drawNote]);
  // V2: what happened while the player was away, under the recap and the plate (then not again at the top of the story).
  const [lines] = useState(() => meanwhileDue(view));
  const close = () => { if (view.dreamt) markSeen(view.id, view.dreamt.at); if (lines) markMeanwhileSeen(view); onDone(); };
  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  });
  return (
    <div className="lm-morning" role="dialog" aria-modal="true" aria-labelledby="lm-morning-h">
      <div className="lm-morning-inner">
        <ClothThumb view={view} />
        <p className="lm-morning-eyebrow">{view.title}</p>
        <h1 id="lm-morning-h">{inPlay ? "While you played, the loom dreamt" : "While you slept, the loom dreamt"}</h1>
        {recap && (
          <p className="lm-morning-recap" onClick={() => setShown(recap.length)} aria-label={recap}>
            <span aria-hidden="true">{done ? recap : recap.slice(0, shown)}</span>
            {!done && <span className="lm-caret" aria-hidden="true" />}
          </p>
        )}
        {done && plates.map((p) => (
          <figure key={p.i} className="lm-plate lm-morning-plate">
            <img className="lm-develop" src={api.imageUrl(view.id, p.i)} alt={p.caption} />
            <figcaption>{p.caption}</figcaption>
            {p.code && <PlateTag src={api.imageUrl(view.id, p.i)} plate={{ i: p.i, code: p.code, caption: p.caption, ...(p.drawnAt ? { drawnAt: p.drawnAt } : {}) }} />}
          </figure>
        ))}
        {done && drawNote && (
          <p className="lm-gate-small">Plates draw you as a man unless your story shows you are a woman. You can choose in <a href="/account" onClick={close}>Account settings</a>.</p>
        )}
        {done && lines && <Meanwhile lines={lines} />}
        {done && view.returning > 0 && (
          <p className="lm-morning-return">
            {view.returning === 1 ? "Something the story let go quiet has been tied back on." : `${view.returning} quiet threads have been tied back on.`} You'll know it when it comes.
          </p>
        )}
        <button type="button" className="lm-cta" onClick={done ? close : () => setShown(recap.length)}>
          {done ? "Back to the story" : "Skip ahead"}
        </button>
      </div>
    </div>
  );
}
